// Renders a FormSchema. This is the only component that knows how to turn
// config into inputs — every funnel screen goes through it, so layout,
// validation display, conditional visibility and grid behaviour are identical
// across all verticals by construction.
import React from "react";
import { Box, Divider, Grid2, Typography } from "@mui/material";
import { evaluate } from "./conditions";
import { allFields as allOf, isRequired } from "./schema";
import { getFieldComponent } from "./registry";

/** @typedef {import("./types").FormSchema} FormSchema */

const MissingField = ({ type, name }) => {
  if (process.env.NODE_ENV === "production") return null;
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: "1px dashed #d4575a",
        color: "#d4575a",
        fontSize: "0.8125rem",
      }}
    >
      Unknown field type <strong>{type}</strong> for “{name}”. Register it with{" "}
      <code>registerField()</code>.
    </Box>
  );
};

/**
 * @param {Object} props
 * @param {FormSchema} props.schema
 * @param {ReturnType<import("./useSchemaForm").default>} props.form
 * @param {boolean} [props.disabled]   Freeze the whole form (e.g. while saving).
 * @param {Object} [props.sx]
 */
const SchemaForm = ({ schema, form, disabled, sx }) => {
  const { values, errors, handleChange, handleBlur } = form;

  return (
    <Box component="form" noValidate onSubmit={(e) => e.preventDefault()} sx={sx}>
      {(schema?.sections || []).map((section, sectionIndex) => {
        if (!evaluate(section.visibleWhen, values)) return null;

        const fields = (section.fields || []).filter((f) =>
          evaluate(f.visibleWhen, values)
        );
        if (!fields.length) return null;

        return (
          <Box key={section.title || sectionIndex} sx={{ mb: 3 }}>
            {sectionIndex > 0 && section.title && (
              <Divider sx={{ mb: 3, borderColor: "rgba(201,167,119,0.22)" }} />
            )}

            {section.title && (
              <Typography
                variant="h6"
                sx={{
                  color: "#3d322b",
                  fontWeight: 700,
                  fontSize: "1.0625rem",
                  letterSpacing: "-0.01em",
                }}
              >
                {section.title}
              </Typography>
            )}
            {section.description && (
              <Typography sx={{ color: "#7B6E66", fontSize: "0.875rem", mt: 0.5, lineHeight: 1.6 }}>
                {section.description}
              </Typography>
            )}

            <Grid2 container columnSpacing={{ xs: 2, md: 3 }}>
              {fields.map((field) => {
                const Component = getFieldComponent(field.type);
                // A dependent list names the first parent still unanswered, so
                // its empty state reads "Select Make first" rather than naming
                // a raw column — or nothing at all when it has several parents.
                const parents = Array.isArray(field.options)
                  ? []
                  : [field.options?.dependsOn ?? []].flat().filter(Boolean);
                const pending = parents.find((name) => !values[name]);
                const pendingParentLabel = pending
                  ? allOf(schema).find((f) => f.name === pending)?.label ||
                    pending.replace(/_/g, " ")
                  : undefined;
                // Fields stack full-width on mobile; `col` (out of 12) only
                // applies from md up, so no config has to repeat breakpoints.
                const span = field.col ?? 6;

                return (
                  <Grid2 key={field.name} size={{ xs: 12, md: span }}>
                    {Component ? (
                      <Component
                        field={{ ...field, required: isRequired(field, values) }}
                        pendingParentLabel={pendingParentLabel}
                        value={values[field.name]}
                        values={values}
                        error={errors[field.name] || ""}
                        // `evaluate` answers "does this condition hold?", and
                        // an absent condition holds — that is what makes
                        // `visibleWhen` optional. Passing an absent
                        // `disabledWhen` through it therefore disabled every
                        // field that didn't define one, which was every field
                        // in the app. Only consult it when it exists.
                        disabled={
                          disabled ||
                          (field.disabledWhen
                            ? evaluate(field.disabledWhen, values)
                            : false)
                        }
                        onChange={handleChange(field.name)}
                        onBlur={handleBlur(field.name)}
                      />
                    ) : (
                      <MissingField type={field.type} name={field.name} />
                    )}
                  </Grid2>
                );
              })}
            </Grid2>
          </Box>
        );
      })}
    </Box>
  );
};

export default SchemaForm;
